import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Building2, AlertTriangle, Clock, FileText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyDocumentsWidget = () => {
  const [data, setData] = useState({ expiring_documents: [], expired_documents: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/company-documents/expiring/summary?days=45`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching company documents:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  const hasAlerts = data.expired_documents.length > 0 || data.expiring_documents.length > 0;

  if (!hasAlerts) {
    return null; // Don't show widget if no alerts
  }

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-600" />
            Company Documents Alert
          </div>
          <Link to="/company-documents">
            <Button variant="outline" size="sm">View All</Button>
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Expired Documents */}
        {data.expired_documents.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="font-semibold text-red-700">
                {data.expired_documents.length} Expired Document{data.expired_documents.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-2">
              {data.expired_documents.slice(0, 3).map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-white rounded p-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-red-500" />
                    <div>
                      <p className="text-sm font-medium">{doc.document_type}</p>
                      <p className="text-xs text-gray-500">{doc.company_name}</p>
                    </div>
                  </div>
                  <Badge className="bg-red-500">
                    Expired {Math.abs(doc.days_until_expiry)}d ago
                  </Badge>
                </div>
              ))}
              {data.expired_documents.length > 3 && (
                <p className="text-xs text-red-600 text-center">
                  +{data.expired_documents.length - 3} more expired documents
                </p>
              )}
            </div>
          </div>
        )}

        {/* Expiring Soon Documents */}
        {data.expiring_documents.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="font-semibold text-amber-700">
                {data.expiring_documents.length} Expiring Within 45 Days
              </span>
            </div>
            <div className="space-y-2">
              {data.expiring_documents.slice(0, 3).map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-white rounded p-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-500" />
                    <div>
                      <p className="text-sm font-medium">{doc.document_type}</p>
                      <p className="text-xs text-gray-500">{doc.company_name}</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-500">
                    {doc.days_until_expiry}d left
                  </Badge>
                </div>
              ))}
              {data.expiring_documents.length > 3 && (
                <p className="text-xs text-amber-600 text-center">
                  +{data.expiring_documents.length - 3} more expiring soon
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyDocumentsWidget;
